/**
 * Premium Invite — `/invitacion-premium`
 *
 * A/B y copy revisables en **Admin → Invitación premium / Reels** (`/admin/premium-invite-config`).
 * Resolución detallada en `usePremiumInviteLayout` + `premiumInviteLayoutAb.ts`.
 */

// BioTemplatePremiumSimple deprecado 2026-06-14 — el operador eligió que
// /invitacion-premium use el mismo template que la home (DualCTAVideoLanding).
// Mantenemos el import comentado y la prop config sin cambios para que un
// rollback sea trivial si hace falta.
// import BioTemplatePremiumSimple from "@/components/templates/BioTemplatePremiumSimple";
import DualCTAVideoLanding from "@/pages/DualCTAVideoLanding";
import ReelsFeed from "@/components/ReelsFeed";
import { useFreshCtaFlags } from "@/hooks/useFreshCtaFlags";
import { usePremiumInviteLayout } from "@/hooks/usePremiumInviteLayout";
import { useSEO } from "@/hooks/useSEO";
import { useTemplateSelection } from "@/hooks/useTemplateSelection";
import {
  isPremiumInviteLandingAllowed,
  PREMIUM_INVITE_BLOCKED_REDIRECT,
  readGalleryPremiumRouteGrantFromSession,
  readPremiumInviteShareTokenFromSearch,
  readShareGrantedFromSession,
  writeShareGrantedToSession,
} from "@/lib/premiumInviteAccess";
import { resolveCheckoutCountryCode } from "@/lib/resolveCheckoutCountryCode";
import { getReelsPlaybackIdsPreset } from "@/lib/reelsPlaybackPreset";
import { resolvePremiumInviteFreeTelegramUrl } from "@/lib/premiumInviteFreeTelegramUrl";
import {
  getVipCheckoutGateway,
  resolvePremiumInviteVipCtaHref,
} from "@/lib/premiumVipCheckoutDestination";
import {
  getServerTemplateConfig,
} from "@/lib/serverTemplateDetection";
import {
  localeForPlansFromMuxLanguage,
  startVipMembershipVideoCheckout,
} from "@/lib/vipMembershipVideoCheckout";
import type { PremiumInvitePublicConfig } from "@/lib/premiumInvitePublicConfig";
import type { ReelsFeedAbVariant } from "@/components/ReelsFeed";
import { getLabelsForLanguage } from "@/config/bioTemplatePremiumPlans";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { getClerkUserImperative } from "@/lib/clerkGate";
import {
  trackComunidadLandingViewPostHog,
  trackComunidadReelsVipClickPostHog,
} from "@/lib/comunidadPostHog";

/**
 * Lee `?preview_show_vip=0|1` y `?preview_show_tg=0|1` del URL (solo para previsualizar
 * desde el admin sin necesidad de guardar). Devuelve null si el param no está presente.
 */
function readCardPreviewParams(): { showVip: boolean | null; showTg: boolean | null } {
  if (typeof window === "undefined") return { showVip: null, showTg: null };
  const s = new URLSearchParams(window.location.search);
  const vip = s.get("preview_show_vip");
  const tg = s.get("preview_show_tg");
  return {
    showVip: vip === "1" ? true : vip === "0" ? false : null,
    showTg: tg === "1" ? true : tg === "0" ? false : null,
  };
}

export default function PremiumInvite() {
  useSEO();
  const { country, language, showCommunity: injectedShowCommunity } =
    useTemplateSelection();
  const { layout, ready, publicConfig } = usePremiumInviteLayout();
  const landingViewTrackedRef = useRef(false);

  // One-time Instagram share token (?ot=). Not burned on GET (IG preview bots).
  // After burn we strip ?ot=; session rehydrates grant on refresh (AR sin community).
  const shareToken = useMemo(() => readPremiumInviteShareTokenFromSearch(), []);
  // Re-read on mount (grant is set on home before navigate; SPA remounts this route).
  // Also poll once after short delay in case grant was written same tick as navigation.
  const [galleryRouteGranted, setGalleryRouteGranted] = useState(() =>
    readGalleryPremiumRouteGrantFromSession(),
  );
  useEffect(() => {
    if (galleryRouteGranted) return;
    const t = window.setTimeout(() => {
      if (readGalleryPremiumRouteGrantFromSession()) setGalleryRouteGranted(true);
    }, 50);
    return () => window.clearTimeout(t);
  }, [galleryRouteGranted]);
  const [shareGranted, setShareGranted] = useState(() =>
    readShareGrantedFromSession(shareToken),
  );
  const [sharePhase, setSharePhase] = useState<
    "idle" | "need_open" | "opening" | "ok" | "error"
  >(() => {
    if (shareToken) {
      return readShareGrantedFromSession(shareToken) ? "ok" : "need_open";
    }
    // Post-strip reload: session still has grant → skip open screen.
    return readShareGrantedFromSession(null) ? "ok" : "idle";
  });
  const [shareError, setShareError] = useState<string | null>(null);

  // Edge country = Worker inject (autoridad). No usar override SPA `?country=`
  // para el gate: un tester fuera de AR no debe bloquearse; un AR real sin
  // community en cta-por-region sí.
  const edgeConfig = useMemo(() => getServerTemplateConfig(), []);
  const edgeCountry = edgeConfig?.country ?? null;
  const isEdgeAr = String(edgeCountry || "").toUpperCase() === "AR";

  const { freshFlags, isFreshLoading } = useFreshCtaFlags({
    enabled: isEdgeAr && !shareGranted,
    countryCode: "AR",
    templateConfig: edgeConfig,
  });

  const showCommunityForAccess =
    freshFlags?.showCommunity ??
    edgeConfig?.showCommunity ??
    injectedShowCommunity;

  const accessAllowed = isPremiumInviteLandingAllowed({
    edgeCountry,
    showCommunity: showCommunityForAccess,
    shareTokenGranted: shareGranted,
    galleryRouteGranted,
  });

  // AR sin community en HTML: no redirigir en el primer paint (isFreshLoading
  // arranca en false). Esperamos cta-flags o un settle corto si el fetch falla.
  // Con ?ot= pendiente de abrir, no redirigimos por geo (el usuario debe tocar Abrir).
  const [arGateSettled, setArGateSettled] = useState(
    () =>
      !isEdgeAr ||
      showCommunityForAccess === true ||
      shareGranted ||
      galleryRouteGranted ||
      Boolean(shareToken),
  );

  useEffect(() => {
    if (shareGranted || shareToken || galleryRouteGranted) {
      setArGateSettled(true);
      return;
    }
    if (!isEdgeAr) {
      setArGateSettled(true);
      return;
    }
    if (showCommunityForAccess === true || freshFlags != null) {
      setArGateSettled(true);
      return;
    }
    if (isFreshLoading) return;
    const t = window.setTimeout(() => setArGateSettled(true), 450);
    return () => window.clearTimeout(t);
  }, [
    isEdgeAr,
    showCommunityForAccess,
    freshFlags,
    isFreshLoading,
    shareGranted,
    shareToken,
    galleryRouteGranted,
  ]);

  const accessPending =
    isEdgeAr && !accessAllowed && !arGateSettled && !shareToken;

  const handleOpenShareInvite = useCallback(async () => {
    if (!shareToken || sharePhase === "opening" || shareGranted) return;
    setSharePhase("opening");
    setShareError(null);
    try {
      const res = await fetch("/api/premium-invite-share/consume", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: shareToken }),
        cache: "no-store",
      });
      const data = (await res.json().catch(() => null)) as {
        ok?: boolean;
        message?: string;
      } | null;
      if (!res.ok || !data?.ok) {
        setSharePhase("error");
        setShareError(
          data?.message ||
            (res.status === 410
              ? "Este link ya fue usado o expiró"
              : "No se pudo abrir la invitación"),
        );
        return;
      }
      writeShareGrantedToSession(shareToken);
      setShareGranted(true);
      setSharePhase("ok");
      // Strip ot from URL so refresh uses session grant (token already burned).
      try {
        const u = new URL(window.location.href);
        u.searchParams.delete("ot");
        u.searchParams.delete("share");
        window.history.replaceState({}, "", u.pathname + u.search + u.hash);
      } catch {
        /* ignore */
      }
    } catch {
      setSharePhase("error");
      setShareError("Error de conexión. Intentá de nuevo.");
    }
  }, [shareToken, sharePhase, shareGranted]);

  // Overrides de previsualización desde URL params (?preview_show_vip=0|1, ?preview_show_tg=0|1).
  // Solo activos en esta ruta — el admin los usa para probar sin guardar.
  const previewParams = useMemo(() => readCardPreviewParams(), []);
  const configWithPreview = useMemo((): PremiumInvitePublicConfig | null => {
    if (!publicConfig || (previewParams.showVip === null && previewParams.showTg === null)) {
      return publicConfig;
    }
    return {
      ...publicConfig,
      ...(previewParams.showVip !== null ? { simple_show_vip_card: previewParams.showVip } : {}),
      ...(previewParams.showTg !== null ? { simple_show_telegram_card: previewParams.showTg } : {}),
    };
  }, [publicConfig, previewParams]);

  // Edge AR / Comunidad: NUNCA caer a US (eso activaba Stripe en VIP).
  // Exterior UNKNOWN → US solo fuera de AR (planes Stripe intl).
  const effectiveCountry = (() => {
    const resolved = resolveCheckoutCountryCode(country);
    if (resolved !== "UNKNOWN") return resolved;
    const edgeCc = String(edgeCountry || "").toUpperCase();
    if (edgeCc === "AR" || showCommunityForAccess) return "AR";
    return "US";
  })();

  const freeTelegramUrl = useMemo(
    () => resolvePremiumInviteFreeTelegramUrl(configWithPreview),
    [configWithPreview],
  );

  const vipMoreHref = useMemo(
    () =>
      resolvePremiumInviteVipCtaHref(
        effectiveCountry,
        configWithPreview?.vip_checkout_base_url,
      ),
    [effectiveCountry, configWithPreview],
  );

  const vipGateway = getVipCheckoutGateway(effectiveCountry);
  const hasVipOverride = Boolean(configWithPreview?.vip_checkout_base_url?.trim());

  // PostHog: view canónico Comunidad + premium_invite_page_view (1× por mount).
  useEffect(() => {
    if (!ready || !accessAllowed || accessPending || landingViewTrackedRef.current) {
      return;
    }
    landingViewTrackedRef.current = true;
    trackComunidadLandingViewPostHog({
      country: effectiveCountry,
      language,
      layout: layout || "premium_simple",
      vip_checkout_gateway: vipGateway,
      has_vip_checkout_override: hasVipOverride,
      has_free_telegram_url: Boolean(freeTelegramUrl),
      mux_overlay_enabled: configWithPreview?.simple_mux_overlay_enabled,
      has_mux_overlay_playback_id: Boolean(
        configWithPreview?.simple_mux_overlay_playback_id?.trim(),
      ),
    });
  }, [
    ready,
    accessAllowed,
    accessPending,
    effectiveCountry,
    language,
    layout,
    vipGateway,
    hasVipOverride,
    freeTelegramUrl,
    configWithPreview?.simple_mux_overlay_enabled,
    configWithPreview?.simple_mux_overlay_playback_id,
  ]);

  // Sin useUser/hooks de Clerk: /invitacion-premium es path diferido
  // (isClerkDeferredPath) — el provider NO monta. Prefill imperativo al click.
  const handleVipCheckout = useCallback(() => {
    trackComunidadReelsVipClickPostHog({
      country: effectiveCountry,
      vip_checkout_gateway: vipGateway,
      has_vip_checkout_override: hasVipOverride,
      cta_destination: vipMoreHref || "stripe_subscription",
    });
    const clerkEmail =
      getClerkUserImperative()?.primaryEmailAddress?.emailAddress;
    void startVipMembershipVideoCheckout({
      countryCode: effectiveCountry,
      pathname: "/invitacion-premium",
      languageForPlans: localeForPlansFromMuxLanguage(language),
      email: clerkEmail || undefined,
    });
  }, [effectiveCountry, language, vipGateway, hasVipOverride, vipMoreHref]);

  const reelsAbOverride = useMemo((): ReelsFeedAbVariant | undefined => {
    const v = configWithPreview?.reels_player_variant ?? "three_teaser";
    if (v === "use_posthog") return undefined;
    return v;
  }, [configWithPreview]);

  const playbackIdsPreset = useMemo(
    () => getReelsPlaybackIdsPreset(configWithPreview),
    [configWithPreview],
  );

  const teaserReelCount = configWithPreview?.reels_teaser_count ?? 3;

  const reelsAbFallback = useMemo(
    () =>
      configWithPreview?.reels_player_variant === "classic" ? "classic" : "three_teaser",
    [configWithPreview],
  );

  const endScreenCtaLabels = useMemo(() => {
    const L = getLabelsForLanguage(language);
    const isEnglish = language.split("-")[0].toLowerCase() === "en";
    return {
      headline:
        configWithPreview?.end_screen_headline?.trim() ||
        (isEnglish ? "Want to keep watching?" : "¿Querés seguir viendo más?"),
      freeTelegram:
        configWithPreview?.end_screen_free_cta_label?.trim() || L.freePlanCta,
      vip: configWithPreview?.end_screen_vip_cta_label?.trim() || L.joinVip,
    };
  }, [configWithPreview, language]);

  const handleReachEnd = useCallback(() => {
    if (import.meta.env.DEV) {
      console.debug("[PremiumInvite] reels reach end");
    }
  }, []);

  // Instagram one-time: pantallita "Abrir invitación" (no quema el token hasta el tap).
  if (shareToken && (sharePhase === "need_open" || sharePhase === "opening" || sharePhase === "error")) {
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center bg-black px-6 text-center">
        <p className="mb-2 text-xs font-medium uppercase tracking-[0.2em] text-white/50">
          Invitación privada
        </p>
        <h1 className="mb-3 max-w-sm text-2xl font-semibold tracking-tight text-white">
          Tu acceso de un solo uso
        </h1>
        <p className="mb-8 max-w-xs text-sm leading-relaxed text-white/65">
          Este link se envió por Instagram y solo se puede abrir una vez. Al
          tocar el botón queda inválido para cualquiera más.
        </p>
        {shareError ? (
          <p className="mb-4 max-w-xs text-sm text-rose-300/95" role="alert">
            {shareError}
          </p>
        ) : null}
        {sharePhase === "error" ? (
          <Link
            to="/"
            className="rounded-full border border-white/20 bg-white/10 px-6 py-3 text-sm font-medium text-white transition hover:bg-white/15"
          >
            Volver al inicio
          </Link>
        ) : (
          <button
            type="button"
            onClick={() => void handleOpenShareInvite()}
            disabled={sharePhase === "opening"}
            className="rounded-full bg-white px-8 py-3.5 text-sm font-semibold text-black transition hover:bg-white/90 active:scale-[0.98] disabled:opacity-60"
          >
            {sharePhase === "opening" ? "Abriendo…" : "Abrir invitación"}
          </button>
        )}
      </div>
    );
  }

  if (accessPending || !ready) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black">
        <div
          className="h-10 w-10 animate-spin rounded-full border-2 border-white/25 border-t-white"
          aria-hidden
        />
      </div>
    );
  }

  // AR sin cta_type=community en la locación (admin/cta-por-region) → home.
  // Exterior siempre permitido. Token one-time ya otorgado → permitido.
  if (!accessAllowed) {
    return <Navigate to={PREMIUM_INVITE_BLOCKED_REDIRECT} replace />;
  }

  if (layout === "reels_feed") {
    return (
      <ReelsFeed
        playbackIdsPreset={playbackIdsPreset}
        abVariantOverride={reelsAbOverride}
        abVariantFallback={reelsAbFallback}
        seeMoreHref={vipMoreHref}
        teaserReelCount={teaserReelCount}
        freeTelegramUrl={freeTelegramUrl}
        vipMoreHref={vipMoreHref}
        onVipCheckoutClick={
          vipGateway === "stripe_subscription" ? handleVipCheckout : undefined
        }
        endScreenCtaLabels={endScreenCtaLabels}
        onVideoPlay={(index, playbackId) => {
          if (import.meta.env.DEV) {
            console.debug(`[PremiumInvite] reel ${index + 1}`, playbackId);
          }
        }}
        onVideoChange={(index, playbackId) => {
          if (import.meta.env.DEV) {
            console.debug(`[PremiumInvite] reel change ${index + 1}`, playbackId);
          }
        }}
        language={language}
        onReachEnd={handleReachEnd}
      />
    );
  }

  // BioTemplatePremiumSimple deprecado — usamos DualCTAVideoLanding (mismo
  // que la home) para coherencia entre / y /invitacion-premium. El componente
  // lee dualCtaGeo del Worker inject; geoMux/geoVip props = cta-flags SPA fallback
  // (corrige HTML stale post-save admin).
  // slotKey separado para que admin pueda asignar variantes distintas en
  // /invitacion-premium vs la home /.
  const geoVipFromFlags =
    freshFlags?.ctaUrl &&
    /mercadopago\.com|mpago\.la|preapproval_plan_id=/i.test(freshFlags.ctaUrl)
      ? freshFlags.ctaUrl
      : undefined;
  return (
    <DualCTAVideoLanding
      slotKey="premium_invite_landing"
      geoMuxPlaybackId={freshFlags?.muxPlaybackId}
      geoMuxSource={freshFlags?.muxSource}
      geoVipHref={geoVipFromFlags}
      geoVipLabel={freshFlags?.ctaLabel?.trim() || undefined}
    />
  );
}
